import React, { useMemo, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import Grid from "@material-ui/core/Grid";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  LabelList,
} from "recharts";

import GridItem from "components/Grid/GridItem.js";
import GridContainer from "components/Grid/GridContainer";
import Greeting from "components/Greeting/Greeting";
//@ts-ignore
import { Map, TileLayer, CircleMarker, Tooltip as LeafletTooltip } from "react-leaflet";
import "components/Map/map-style.css";
import SpinLoading from "components/Loading/SpinLoading.js";
import Button from "@material-ui/core/Button";
import GetAppIcon from "@material-ui/icons/GetApp";
import { useRootSelector } from "hooks/reduxHooks";
import Reading from "spectral-core/models/Reading";
import { useUserLoggedIn } from "hooks/userHooks";
import { useGetReadings } from "hooks/readingsHooks";
import {
  filterNutroScanReadings,
  aggregateNutroScanOverview,
  countReadingsWithLabResult,
} from "utils/nutroScanAggregates";
import { cultureTypeLabelPt } from "spectral-core/cultureEnum";
import { exportMultiSheetExcel } from "utils/excelExport";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import Select from "@material-ui/core/Select";
import { Checkbox, ListItemText, MenuItem, TextField } from "@material-ui/core";
import { getDiretoria } from "utils/diretoriaMap";
import intl from "react-intl-universal";

const CHART_BAR_COLORS = [
  "#00838f", "#26a69a", "#43a047", "#66bb6a",
  "#8bc34a", "#cddc39", "#ffb300", "#fb8c00",
  "#ef5350", "#ab47bc", "#5c6bc0", "#29b6f6",
];

//@ts-ignore
const useStyles = makeStyles({
  chartContainer: {
    padding: "16px",
    marginBottom: "16px",
  },
  chartTitle: {
    fontSize: "15px",
    fontWeight: 600,
    color: "#37474f",
    marginBottom: "8px",
  },
  kpiCard: {
    padding: "16px",
    textAlign: "center" as const,
    borderLeft: "4px solid #00838f",
    height: 110,
    display: "flex",
    flexDirection: "column" as const,
    justifyContent: "center",
    alignItems: "center",
  },
  kpiCardMain: {
    padding: "16px",
    textAlign: "center" as const,
    borderLeft: "4px solid #00838f",
    backgroundColor: "#e0f7fa",
    height: 110,
    display: "flex",
    flexDirection: "column" as const,
    justifyContent: "center",
    alignItems: "center",
  },
  filters: {
    padding: "12px 16px",
    marginBottom: 12,
    display: "flex",
    gap: 12,
    flexWrap: "wrap",
    alignItems: "flex-end",
  }
});

export default function Dashboard() {
  const classes = useStyles();

  const isLoggedIn = useUserLoggedIn();
  const { isFetching } = useGetReadings();

  const user = useRootSelector((state) => state.user.currentUser);
  const rawReadings = useRootSelector((state) => state.reading.readings) as Reading[] | undefined;

  const [filterDirectory, setFilterDirectory] = useState<string>("");
  const [filterUser, setFilterUser] = useState<string[]>([]);
  const [filterDateFrom, setFilterDateFrom] = useState<string>("");
  const [filterDateTo, setFilterDateTo] = useState<string>("");

  const baseReadings = useMemo(
    () => (rawReadings ? filterNutroScanReadings(rawReadings, true) : []),
    [rawReadings]);

  const availableDirectories = useMemo(() => {
    const set = new Set<string>();
    for (const r of baseReadings) {
      const d = getDiretoria(r.userName);
      if (d) set.add(d);
    }
    return Array.from(set).sort();
  }, [baseReadings]);

  const afterDirectory = useMemo(() => {
    if (!filterDirectory) return baseReadings;
    return baseReadings.filter((r) => getDiretoria(r.userName) === filterDirectory);
  }, [baseReadings, filterDirectory])

  const availableUsers = useMemo(() => {
    const set = new Set<string>();
    for (const r of afterDirectory) {
      const user = (r.userName || "").trim();
      if (user) set.add(user);
    }
    return Array.from(set).sort();
  }, [afterDirectory]);

  const afterUser = useMemo(() => {
    if (filterUser.length === 0) return afterDirectory;
    return afterDirectory.filter((r) => filterUser.includes((r.userName || "").trim()));
  }, [afterDirectory, filterUser]);

  const filteredReadings = useMemo(() => {
    let f = afterUser;
    if (filterDateFrom) {
      const from = new Date(filterDateFrom + "T00:00:00");
      f = f.filter((r) => {
        const dt = r.dateTime;
        if (!dt) return false;
        const [d, m, y] = dt.split(" ")[0].split("/").map(Number);
        return new Date(y, m - 1, d) >= from;
      });
    }
    if (filterDateTo) {
      const to = new Date(filterDateTo + "T23:59:59");
      f = f.filter((r) => {
        const dt = r.dateTime;
        if (!dt) return false;
        const [d, m, y] = dt.split(" ")[0].split("/").map(Number);
        return new Date(y, m - 1, d) <= to;
      });
    }
    return f;
  }, [afterUser, filterDateFrom, filterDateTo]);

  const overview = useMemo(
    () => (filteredReadings.length ? aggregateNutroScanOverview(filteredReadings) : null),
  [filteredReadings]);

  const monthData = overview
    ? Object.keys(overview.byMonth).sort().map((k) => ({ name: k, value: overview.byMonth[k] }))
    : [];

  const cultureData = overview
    ? Object.entries(overview.byCulture)
        .map(([name, value]) => ({ name, value }))
        .sort((a, b) => b.value - a.value)
    : [];

  // Mapa com pins por cultura
  const cultureColorMap = useMemo(() => {
    const map: Record<string, string> = {};
    let colorIdx = 0;
    for (const r of filteredReadings) {
      const cult = cultureTypeLabelPt(r.culture, r.customCultureName) || "—";
      if (!map[cult]) {
        map[cult] = CHART_BAR_COLORS[colorIdx % CHART_BAR_COLORS.length];
        colorIdx++;
      }
    }
    return map;
  }, [filteredReadings]);

  const mapPins = useMemo(() => {
    const out: { position: [number, number]; label: string; culture: string; color: string }[] = [];
    for (const r of filteredReadings) {
      try {
        if (!r.geoLocation || r.geoLocation.length < 10) continue;
        const { Latitude, Longitude } = JSON.parse(r.geoLocation);
        const cult = cultureTypeLabelPt(r.culture, r.customCultureName) || "—";
        out.push({
          position: [Latitude, Longitude],
          label: r.name || r.sampleCode || "—",
          culture: cult,
          color: cultureColorMap[cult] || "#9e9e9e",
        });
      } catch { /* skip */ }
    }
    return out;
  }, [filteredReadings, cultureColorMap]);

  // Legenda com contagem por cultura (mesmo padrão da aba Coordenadas)
  const legendEntries = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const p of mapPins) counts[p.culture] = (counts[p.culture] || 0) + 1;
    return Object.entries(counts)
      .sort((a, b) => b[1] - a[1])
      .map(([name, count]) => ({
        name,
        count,
        color: cultureColorMap[name] || "#9e9e9e",
      }));
  }, [mapPins, cultureColorMap]);

  const labResultsCount = useMemo(() => 
    (countReadingsWithLabResult(filteredReadings)), 
  [filteredReadings]);

  const dataLoading = !isLoggedIn || rawReadings === undefined;

  return (
    <div>
      {user && <Greeting user={user} />}

      {dataLoading && (
        <div style={{ textAlign: "center", padding: 40 }}>
          <SpinLoading />
          <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
            Carregando análises...
          </Typography>
        </div>
      )}

      {!dataLoading && (<>
      <Paper className={classes.filters} elevation={0}>
        <FormControl style={{ minWidth: 160, flex: "1 1 160px" }} size="small">
          <InputLabel id="dash-filter-directory" shrink>{intl.get("filters.directory")}</InputLabel>
          <Select
            labelId="dash-filter-directory"
            value={filterDirectory}
            onChange={(e: React.ChangeEvent<{ value: unknown }>) => {
              setFilterDirectory(e.target.value as string);
              setFilterUser([]);
            }}
            displayEmpty
          >
            <MenuItem value="">{intl.get("filters.all_feminine")}</MenuItem>
            {availableDirectories.map((d) => (
              <MenuItem key={d} value={d}>{d}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl style={{ minWidth: 160, flex: "1 1 160px" }} size="small">
          <InputLabel id="dash-filter-user" shrink>{intl.get("filters.user")}</InputLabel>
          <Select
            labelId="dash-filter-user"
            multiple
            value={filterUser}
            onChange={(e: React.ChangeEvent<{ value: unknown }>) => {
              setFilterUser(e.target.value as string[]);
            }}
            displayEmpty
            renderValue={(selected) => {
              const sel = selected as string[];
              if (sel.length === 0) return intl.get("filters.all");
              if (sel.length === 1) return sel[0];
              return `${sel.length} ${intl.get("filters.users")}`;
            }}
          >
            {availableUsers.map((u) => (
              <MenuItem key={u} value={u}>
                <Checkbox checked={filterUser.indexOf(u) > -1} size="small" color="primary" />
                <ListItemText primary={u} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField 
          label={intl.get("filters.from")} 
          type="date" 
          size="small" 
          value={filterDateFrom} 
          onChange={(e) => setFilterDateFrom(e.target.value)} 
          InputLabelProps={{ shrink: true }} 
          style={{ width: 145, flex: "0 0 145px"}}
        />
        <TextField 
          label={intl.get("filters.to")} 
          type="date" 
          size="small" 
          value={filterDateTo} 
          onChange={(e) => setFilterDateTo(e.target.value)} 
          InputLabelProps={{ shrink: true }} 
          style={{ width: 145, flex: "0 0 145px"}}
        />
      </Paper>
      {/* Export Excel */}
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
        <Button
          variant="outlined"
          color="primary"
          startIcon={<GetAppIcon />}
          disabled={!overview}
          onClick={() => {
            if (!overview) return;
            const kpis = [
              { metrica: intl.get("nutroscan.total_reads"), valor: overview.totalReadings },
              { metrica: intl.get("nutroscan.last_sync"), valor: overview.lastSync ? overview.lastSync.toLocaleString() : "—" },
              { metrica: intl.get("nutroscan.lab_results"), valor: labResultsCount },
              { metrica: intl.get("nutroscan.avg_temp"), valor: overview.avgTemperature != null ? overview.avgTemperature.toFixed(1) : "—" },
            ];
            const meses = monthData.map((m) => ({ mes: m.name, quantidade: m.value }));
            const culturas = cultureData.map((c) => ({ cultura: c.name, quantidade: c.value }));
            const pinsRows = mapPins.map((p) => ({ nome: p.label, cultura: p.culture, latitude: p.position[0], longitude: p.position[1] }));
            exportMultiSheetExcel(
              [
                { name: "KPIs", data: kpis, columns: [{ header: "Métrica", field: "metrica" }, { header: "Valor", field: "valor" }] },
                { name: "Por Mes", data: meses, columns: [{ header: "Mês", field: "mes" }, { header: "Quantidade", field: "quantidade" }] },
                { name: "Por Cultura", data: culturas, columns: [{ header: "Cultura", field: "cultura" }, { header: "Quantidade", field: "quantidade" }] },
                { name: intl.get("nutroscan.tab_map"), data: pinsRows, columns: [{ header: "Nome", field: "nome" }, { header: "Cultura", field: "cultura" }, { header: "Latitude", field: "latitude" }, { header: "Longitude", field: "longitude" }] },
              ],
              "VisaoGeral"
            );
          }}
        >
          Exportar Excel
        </Button>
      </div>

      {/* KPIs */}
      <Grid container spacing={2} style={{ marginBottom: 16 }}>
        <Grid item xs={12} sm={6} md={3} lg={3}>
          <Paper className={classes.kpiCardMain}>
            <Typography variant="caption">
              {intl.get("nutroscan.total_reads")}{isFetching && ` (${intl.get("nutroscan.loading")}...)`}
            </Typography>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              {isFetching && <SpinLoading />}
              <Typography variant="h4" style={{ fontWeight: 700, color: "#00838f" }}>
                {overview?.totalReadings ?? 0}
              </Typography>
            </div>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3} lg={3}>
          <Paper className={classes.kpiCard}>
            <Typography variant="caption">{intl.get("nutroscan.lab_results")}</Typography>
            <Typography variant="h6">
              { labResultsCount }
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3} lg={3}>
          <Paper className={classes.kpiCard}>
            <Typography variant="caption">{intl.get("nutroscan.last_sync")}</Typography>
            <Typography variant="body1">
              {overview?.lastSync ? overview.lastSync.toLocaleString() : "—"}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3} lg={3}>
          <Paper className={classes.kpiCard}>
            <Typography variant="caption">{intl.get("nutroscan.avg_temp")}</Typography>
            <Typography variant="h6">
              {overview?.avgTemperature != null
                ? overview.avgTemperature.toFixed(1) + " °C"
                : "—"}
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Gráficos: Volume por mês + por cultura */}
      <GridContainer>
        {/*@ts-ignore*/}
        <GridItem xs={12} sm={12} md={6}>
          <Paper className={classes.chartContainer}>
            <Typography className={classes.chartTitle}>{intl.get("nutroscan.analysis_per_month")}</Typography>
            {monthData.length ? (
              <ResponsiveContainer width="100%" height={Math.max(250, monthData.length * 38 + 40)}>
                <BarChart data={monthData} layout="vertical" margin={{ top: 5, right: 40, bottom: 5, left: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 12 }} />
                  <YAxis type="category" dataKey="name" width={80} tick={{ fontSize: 12 }} />
                  <Tooltip formatter={(val: any) => [val, "Quantidade"]} contentStyle={{ borderRadius: 8 }} />
                  <Bar dataKey="value" radius={[0, 4, 4, 0]} maxBarSize={28}>
                    {monthData.map((_e, idx) => (
                      <Cell key={idx} fill={CHART_BAR_COLORS[idx % CHART_BAR_COLORS.length]} />
                    ))}
                    <LabelList dataKey="value" position="right" style={{ fontSize: 12, fontWeight: 500 }} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <Typography variant="body2" color="textSecondary" style={{ textAlign: "center", padding: 40 }}>{intl.get("nutroscan.no_data")}</Typography>
            )}
          </Paper>
        </GridItem>
        {/*@ts-ignore*/}
        <GridItem xs={12} sm={12} md={6}>
          <Paper className={classes.chartContainer}>
            <Typography className={classes.chartTitle}>{intl.get("nutroscan.analysis_per_culture")}</Typography>
            {cultureData.length ? (
              <ResponsiveContainer width="100%" height={Math.max(250, cultureData.length * 38 + 40)}>
                <BarChart data={cultureData} layout="vertical" margin={{ top: 5, right: 40, bottom: 5, left: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 12 }} />
                  <YAxis type="category" dataKey="name" width={120} tick={{ fontSize: 12 }} />
                  <Tooltip formatter={(val: any) => [val, "Quantidade"]} contentStyle={{ borderRadius: 8 }} />
                  <Bar dataKey="value" radius={[0, 4, 4, 0]} maxBarSize={28}>
                    {cultureData.map((_e, idx) => (
                      <Cell key={idx} fill={CHART_BAR_COLORS[idx % CHART_BAR_COLORS.length]} />
                    ))}
                    <LabelList dataKey="value" position="right" style={{ fontSize: 12, fontWeight: 500 }} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <Typography variant="body2" color="textSecondary" style={{ textAlign: "center", padding: 40 }}>{intl.get("nutroscan.no_data")}</Typography>
            )}
          </Paper>
        </GridItem>
      </GridContainer>

      {/* Mapa */}
      <GridContainer>
        {/*@ts-ignore*/}
        <GridItem xs={12}>
          <Paper className={classes.chartContainer}>
            <Typography className={classes.chartTitle}>{intl.get("nutroscan.map_analysis_per_culture")}</Typography>
            {/* Mapa + Legenda lado a lado (mesmo padrão da aba Coordenadas) */}
            <div style={{ display: "flex", gap: 16, marginTop: 8 }}>
              <div style={{ flex: 1, height: "50vh", minWidth: 0 }}>
                {mapPins.length ? (
                  <Map
                    center={mapPins[0].position}
                    zoom={5}
                    style={{ height: "100%", borderRadius: 4 }}
                  >
                    <TileLayer
                        url={`https://basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}.png?key=cb1_3vnf_2_b4ced25f80bd977161a67037`}
                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>'
                    />
                    {mapPins.map((pin, i) => (
                      <CircleMarker
                        key={i}
                        center={pin.position}
                        radius={6}
                        fillOpacity={0.7}
                        color={pin.color}
                        fillColor={pin.color}
                      >
                        <LeafletTooltip>
                          <span>{pin.label}</span><br />
                          <span>{pin.culture}</span>
                        </LeafletTooltip>
                      </CircleMarker>
                    ))}
                  </Map>
                ) : (
                  <Typography variant="body2" color="textSecondary" style={{ textAlign: "center", padding: 40 }}>
                    Sem coordenadas nas leituras carregadas.
                  </Typography>
                )}
              </div>

              {/* Legenda à direita */}
              {legendEntries.length > 0 && (
                <div style={{
                  width: 220,
                  flexShrink: 0,
                  maxHeight: "50vh",
                  overflowY: "auto",
                  paddingRight: 4,
                }}>
                  <Typography variant="subtitle2" style={{ marginBottom: 8, fontWeight: 600, color: "#37474f" }}>
                    Cultura
                  </Typography>
                  {legendEntries.map((entry) => (
                    <div
                      key={entry.name}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                        marginBottom: 6,
                        fontSize: 13,
                        lineHeight: 1.3,
                      }}
                    >
                      <span style={{
                        width: 14,
                        height: 14,
                        borderRadius: "50%",
                        backgroundColor: entry.color,
                        display: "inline-block",
                        flexShrink: 0,
                      }} />
                      <span style={{ fontWeight: 500 }}>{entry.name}</span>
                      <span style={{ color: "#666", whiteSpace: "nowrap" }}>({entry.count})</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </Paper>
        </GridItem>
      </GridContainer>
      </>)}
    </div>
  );
}
